import React, { useState, useEffect } from 'react';
import { 
  Calendar, Users, Clock, Plus, Edit, Eye, EyeOff, Trash2, Search,
  User, UserPlus, CheckCircle, XCircle, AlertCircle, ChevronDown,
  X, Save
} from 'lucide-react';
import { useAuth } from '../../../../context/AuthContext';

const ShiftsTeams = () => {
  const { currentUser } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL || 'https://ksp3plhq.com/api';
  
  // Tab state
  const [activeTab, setActiveTab] = useState('shifts');
  
  // Data states
  const [shifts, setShifts] = useState([]);
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // UI states
  const [showShiftForm, setShowShiftForm] = useState(false);
  const [showTeamForm, setShowTeamForm] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [editingTeam, setEditingTeam] = useState(null);
  
  // User assignment states
  const [showAssignUsersModal, setShowAssignUsersModal] = useState(false);
  const [assigningToId, setAssigningToId] = useState(null);
  const [assigningToType, setAssigningToType] = useState(null); // 'shift' or 'team'
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [searchUserTerm, setSearchUserTerm] = useState('');
  
  // Form states
  const [shiftForm, setShiftForm] = useState({
    name: '',
    hours: '09:00-17:00',
    approved_hours: '08:00-18:00',
    days: [],
    approved_days: [],
    manager: '',
    alternate_manager: ''
  });
  
  const [teamForm, setTeamForm] = useState({
    name: '',
    manager: ''
  });
  
  // Fetch users on component mount
  useEffect(() => {
    fetchUsers();
  }, []);

  // Fetch shifts or teams when tab changes
  useEffect(() => {
    if (activeTab === 'shifts') {
      fetchShifts();
    } else {
      fetchTeams();
    }
  }, [activeTab]);
  
  // Fetch users
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }
      
      const data = await response.json();
      setUsers(data);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch shifts
  const fetchShifts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/shifts`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch shifts');
      }
      
      const data = await response.json();
      setShifts(data);
    } catch (err) {
      console.error('Error fetching shifts:', err);
      setError('Failed to load shifts. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch teams
  const fetchTeams = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/teams`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch teams');
      }
      
      const data = await response.json();
      setTeams(data);
    } catch (err) {
      console.error('Error fetching teams:', err);
      setError('Failed to load teams. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle shift form submission
  const handleShiftSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const method = editingShift ? 'PUT' : 'POST';
      const url = editingShift 
        ? `${API_URL}/shifts/${editingShift.id}` 
        : `${API_URL}/shifts`;
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: shiftForm.name,
          hours: shiftForm.hours,
          approved_hours: shiftForm.approved_hours,
          days: shiftForm.days,
          approved_days: shiftForm.approved_days,
          manager: shiftForm.manager || null,
          alternate_manager: shiftForm.alternate_manager || null
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save shift');
      }
      
      setSuccess(editingShift ? 'Shift updated successfully' : 'Shift created successfully');
      setShowShiftForm(false);
      setEditingShift(null);
      
      // Reset form
      setShiftForm({
        name: '',
        hours: '09:00-17:00',
        approved_hours: '08:00-18:00',
        days: [],
        approved_days: [],
        manager: '',
        alternate_manager: ''
      });
      
      // Refresh shifts
      fetchShifts();
      
    } catch (err) {
      console.error('Error saving shift:', err);
      setError(err.message || 'Failed to save shift. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle team form submission
  const handleTeamSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const method = editingTeam ? 'PUT' : 'POST';
      const url = editingTeam 
        ? `${API_URL}/teams/${editingTeam.id}` 
        : `${API_URL}/teams`;
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: teamForm.name,
          manager: teamForm.manager || null
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save team');
      }
      
      setSuccess(editingTeam ? 'Team updated successfully' : 'Team created successfully');
      setShowTeamForm(false);
      setEditingTeam(null);
      
      // Reset form
      setTeamForm({
        name: '',
        manager: ''
      });
      
      // Refresh teams
      fetchTeams();
      
    } catch (err) {
      console.error('Error saving team:', err);
      setError(err.message || 'Failed to save team. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Open the assign users modal
  const handleOpenAssignUsers = (id, type) => {
    setAssigningToId(id);
    setAssigningToType(type);
    
    // Pre-select currently assigned users
    if (type === 'shift') {
      const shift = shifts.find(s => s.id === id);
      setSelectedUserIds(shift ? [...shift.users] : []);
    } else {
      const team = teams.find(t => t.id === id);
      setSelectedUserIds(team ? [...team.users] : []);
    }
    
    setShowAssignUsersModal(true);
    setSearchUserTerm('');
  };
  
  // Save user assignments
  const handleSaveAssignments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const url = assigningToType === 'shift'
        ? `${API_URL}/shifts/${assigningToId}/users`
        : `${API_URL}/teams/${assigningToId}/users`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ users: selectedUserIds })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to assign users');
      }
      
      // Success message
      setSuccess(`Users assigned to ${assigningToType} successfully`);
      
      // Refresh data
      if (assigningToType === 'shift') {
        fetchShifts();
      } else {
        fetchTeams();
      }
      
      setShowAssignUsersModal(false);
      
    } catch (err) {
      console.error('Error assigning users:', err);
      setError(err.message || 'Failed to assign users. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Toggle user selection for assignment
  const toggleUserSelection = (userId) => {
    if (selectedUserIds.includes(userId)) {
      setSelectedUserIds(selectedUserIds.filter(id => id !== userId));
    } else {
      setSelectedUserIds([...selectedUserIds, userId]);
    }
  };
  
  // Delete a shift
  const handleDeleteShift = async (id) => {
    if (window.confirm('Are you sure you want to delete this shift?')) {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        const response = await fetch(`${API_URL}/shifts/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to delete shift');
        }
        
        setSuccess('Shift deleted successfully');
        fetchShifts();
        
      } catch (err) {
        console.error('Error deleting shift:', err);
        setError(err.message || 'Failed to delete shift. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };
  
  // Delete a team
  const handleDeleteTeam = async (id) => {
    if (window.confirm('Are you sure you want to delete this team?')) {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        const response = await fetch(`${API_URL}/teams/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to delete team');
        }
        
        setSuccess('Team deleted successfully');
        fetchTeams();
        
      } catch (err) {
        console.error('Error deleting team:', err);
        setError(err.message || 'Failed to delete team. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };
  
  // Get user name by ID
  const getUserNameById = (id) => {
    const user = users.find(u => u.id === id);
    return user ? (user.full_name || user.username) : 'Unknown User';
  };
  
  // Format days array for display
  const formatDays = (days) => {
    if (!days || days.length === 0) return 'None';
    if (days.length === 7) return 'All days';
    
    return days.join(', ');
  };
  
  // Handle form open for new shift
  const handleNewShift = () => {
    setEditingShift(null);
    setShiftForm({
      name: '',
      hours: '09:00-17:00',
      approved_hours: '08:00-18:00',
      days: [],
      approved_days: [],
      manager: '',
      alternate_manager: ''
    });
    setShowShiftForm(true);
    setShowTeamForm(false);
  };
  
  // Handle form open for edit shift
  const handleEditShift = (shift) => {
    setEditingShift(shift);
    setShiftForm({
      name: shift.name,
      hours: shift.hours,
      approved_hours: shift.approved_hours,
      days: shift.days,
      approved_days: shift.approved_days,
      manager: shift.manager || '',
      alternate_manager: shift.alternate_manager || ''
    });
    setShowShiftForm(true);
    setShowTeamForm(false);
  };
  
  // Handle form open for new team
  const handleNewTeam = () => {
    setEditingTeam(null);
    setTeamForm({
      name: '',
      manager: ''
    });
    setShowTeamForm(true);
    setShowShiftForm(false);
  };
  
  // Handle form open for edit team
  const handleEditTeam = (team) => {
    setEditingTeam(team);
    setTeamForm({
      name: team.name,
      manager: team.manager || ''
    });
    setShowTeamForm(true);
    setShowShiftForm(false);
  };
  
  // Reset forms
  const resetForms = () => {
    setShowShiftForm(false);
    setShowTeamForm(false);
    setEditingShift(null);
    setEditingTeam(null);
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-slate-200">Shifts & Teams Management</h2>
      
      {/* Tab Navigation */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
        <div className="flex border-b border-slate-700">
          <button
            className={`py-3 px-6 text-sm font-medium ${
              activeTab === 'shifts'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-slate-400 hover:text-slate-300'
            }`}
            onClick={() => setActiveTab('shifts')}
          >
            <div className="flex items-center">
              <Clock className="w-4 h-4 mr-2" />
              Shifts
            </div>
          </button>
          
          <button
            className={`py-3 px-6 text-sm font-medium ${
              activeTab === 'teams'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-slate-400 hover:text-slate-300'
            }`}
            onClick={() => setActiveTab('teams')}
          >
            <div className="flex items-center">
              <Users className="w-4 h-4 mr-2" />
              Teams
            </div>
          </button>
        </div>
        
        {/* Tab Content */}
        <div className="p-6">
          {/* Success Message */}
          {success && (
            <div className="mb-6 bg-green-900/30 border border-green-800 text-green-300 px-4 py-3 rounded-md flex items-start">
              <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
              <div>{success}</div>
              <button 
                onClick={() => setSuccess(null)} 
                className="ml-auto text-green-300 hover:text-green-100"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          
          {/* Error Message */}
          {error && (
            <div className="mb-6 bg-red-900/30 border border-red-800 text-red-300 px-4 py-3 rounded-md flex items-start">
              <AlertCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
              <div>{error}</div>
              <button 
                onClick={() => setError(null)} 
                className="ml-auto text-red-300 hover:text-red-100"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          
          {/* Shifts Tab Content */}
          {activeTab === 'shifts' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-medium text-slate-200">Manage Shifts</h3>
                
                <button 
                  onClick={handleNewShift}
                  className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 
                           text-white rounded-md transition-colors text-sm"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create New Shift
                </button>
              </div>
              
              {/* Shifts List */}
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                </div>
              ) : shifts.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Clock className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p>No shifts found. Create your first shift to get started.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {shifts.map(shift => (
                    <div 
                      key={shift.id} 
                      className="bg-slate-700/40 border border-slate-600 rounded-md p-4"
                    >
                      <div className="flex justify-between">
                        <div>
                          <h4 className="text-lg font-medium text-slate-200">{shift.name}</h4>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                            <div>
                              <p className="text-sm text-slate-400 mb-1">Working Hours:</p>
                              <p className="text-sm text-slate-300">{shift.hours}</p>
                              
                              <p className="text-sm text-slate-400 mb-1 mt-3">Approved Hours:</p>
                              <p className="text-sm text-slate-300">{shift.approved_hours}</p>
                            </div>
                            
                            <div>
                              <p className="text-sm text-slate-400 mb-1">Working Days:</p>
                              <p className="text-sm text-slate-300">{formatDays(shift.days)}</p>
                              
                              <p className="text-sm text-slate-400 mb-1 mt-3">Approved Days:</p>
                              <p className="text-sm text-slate-300">{formatDays(shift.approved_days)}</p>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                            <div>
                              <p className="text-sm text-slate-400 mb-1">Manager:</p>
                              <p className="text-sm text-slate-300">
                                {shift.manager ? getUserNameById(shift.manager) : 'None'}
                              </p>
                            </div>
                            
                            <div>
                              <p className="text-sm text-slate-400 mb-1">Alternate Manager:</p>
                              <p className="text-sm text-slate-300">
                                {shift.alternate_manager ? getUserNameById(shift.alternate_manager) : 'None'}
                              </p>
                            </div>
                          </div>
                          
                          <div className="mt-4">
                            <p className="text-sm text-slate-400 mb-2">Assigned Users ({shift.users.length}):</p>
                            {shift.users.length > 0 ? (
                              <div className="flex flex-wrap gap-2">
                                {shift.users.map(userId => (
                                  <span 
                                    key={userId} 
                                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs 
                                            bg-blue-900/30 text-blue-300 border border-blue-800/50"
                                  >
                                    <User className="w-3 h-3 mr-1" />
                                    {getUserNameById(userId)}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-slate-500 italic">No users assigned</p>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex flex-col space-y-2 ml-4">
                          <button
                            onClick={() => handleEditShift(shift)}
                            className="p-2 text-slate-400 hover:text-slate-200 hover:bg-slate-600/30 rounded"
                            title="Edit Shift"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          
                          <button
                            onClick={() => handleDeleteShift(shift.id)}
                            className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-600/30 rounded"
                            title="Delete Shift"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                          
                          <button
                            onClick={() => handleOpenAssignUsers(shift.id, 'shift')}
                            className="p-2 text-slate-400 hover:text-green-400 hover:bg-slate-600/30 rounded"
                            title="Assign Users"
                          >
                            <UserPlus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Shift Form Modal */}
              {showShiftForm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                  <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                    <div className="p-4 border-b border-slate-700 flex justify-between items-center sticky top-0 bg-slate-800 z-10">
                      <h3 className="text-lg font-medium text-slate-200">
                        {editingShift ? 'Edit Shift' : 'Create New Shift'}
                      </h3>
                      <button 
                        onClick={() => setShowShiftForm(false)}
                        className="text-slate-400 hover:text-slate-200"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    
                    <form onSubmit={handleShiftSubmit} className="p-6 space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                          Shift Name*
                        </label>
                        <input
                          type="text"
                          value={shiftForm.name}
                          onChange={(e) => setShiftForm({...shiftForm, name: e.target.value})}
                          className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md 
                                  text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g. Morning Shift"
                          required
                        />
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">
                            Working Hours*
                          </label>
                          <input
                            type="text"
                            value={shiftForm.hours}
                            onChange={(e) => setShiftForm({...shiftForm, hours: e.target.value})}
                            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md 
                                    text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="e.g. 09:00-17:00"
                            required
                          />
                          <p className="text-xs text-slate-400 mt-1">Format: HH:MM-HH:MM (24-hour)</p>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">
                            Approved Hours*
                          </label>
                          <input
                            type="text"
                            value={shiftForm.approved_hours}
                            onChange={(e) => setShiftForm({...shiftForm, approved_hours: e.target.value})}
                            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md 
                                    text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="e.g. 08:00-18:00"
                            required
                          />
                          <p className="text-xs text-slate-400 mt-1">Format: HH:MM-HH:MM (24-hour)</p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">
                            Working Days*
                          </label>
                          <div className="space-y-2 bg-slate-700 border border-slate-600 rounded-md p-3">
                            {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map(day => (
                              <div key={day} className="flex items-center">
                                <input
                                  type="checkbox"
                                  id={`day-${day}`}
                                  checked={shiftForm.days.includes(day)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setShiftForm({...shiftForm, days: [...shiftForm.days, day]});
                                    } else {
                                      setShiftForm({
                                        ...shiftForm, 
                                        days: shiftForm.days.filter(d => d !== day)
                                      });
                                    }
                                  }}
                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 
                                          border-slate-500 rounded bg-slate-700"
                                />
                                <label htmlFor={`day-${day}`} className="ml-2 text-sm text-slate-300">
                                  {day}
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">
                            Approved Days*
                          </label>
                          <div className="space-y-2 bg-slate-700 border border-slate-600 rounded-md p-3">
                            {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map(day => (
                              <div key={day} className="flex items-center">
                                <input
                                  type="checkbox"
                                  id={`approved-day-${day}`}
                                  checked={shiftForm.approved_days.includes(day)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setShiftForm({...shiftForm, approved_days: [...shiftForm.approved_days, day]});
                                    } else {
                                      setShiftForm({
                                        ...shiftForm, 
                                        approved_days: shiftForm.approved_days.filter(d => d !== day)
                                      });
                                    }
                                  }}
                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 
                                          border-slate-500 rounded bg-slate-700"
                                />
                                <label htmlFor={`approved-day-${day}`} className="ml-2 text-sm text-slate-300">
                                  {day}
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">
                            Manager*
                          </label>
                          <select
                            value={shiftForm.manager}
                            onChange={(e) => setShiftForm({...shiftForm, manager: e.target.value})}
                            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md 
                                    text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          >
                            <option value="">Select Manager</option>
                            {users
                              .filter(user => user.role === 'admin' || user.role === 'manager')
                              .map(user => (
                                <option key={user.id} value={user.id}>
                                  {user.full_name || user.username}
                                </option>
                              ))
                            }
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-slate-300 mb-2">
                            Alternate Manager (Optional)
                          </label>
                          <select
                            value={shiftForm.alternate_manager}
                            onChange={(e) => setShiftForm({...shiftForm, alternate_manager: e.target.value})}
                            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md 
                                    text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">None</option>
                            {users
                              .filter(user => user.role === 'admin' || user.role === 'manager')
                              .map(user => (
                                <option key={user.id} value={user.id}>
                                  {user.full_name || user.username}
                                </option>
                              ))
                            }
                          </select>
                        </div>
                      </div>
                      
                      <div className="flex justify-end space-x-3 pt-4 border-t border-slate-700">
                        <button
                          type="button"
                          onClick={() => setShowShiftForm(false)}
                          className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 
                                   rounded-md transition-colors"
                        >
                          Cancel
                        </button>
                        
                        <button
                          type="submit"
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white 
                                   rounded-md transition-colors flex items-center"
                          disabled={loading}
                        >
                          {loading ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Processing...
                            </>
                          ) : (
                            <>
                              <Save className="w-4 h-4 mr-2" />
                              {editingShift ? 'Update Shift' : 'Create Shift'}
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Teams Tab Content */}
          {activeTab === 'teams' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-medium text-slate-200">Manage Teams</h3>
                
                <button 
                  onClick={handleNewTeam}
                  className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 
                           text-white rounded-md transition-colors text-sm"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create New Team
                </button>
              </div>
              
              {/* Teams List */}
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                </div>
              ) : teams.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p>No teams found. Create your first team to get started.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {teams.map(team => (
                    <div 
                      key={team.id} 
                      className="bg-slate-700/40 border border-slate-600 rounded-md p-4"
                    >
                      <div className="flex justify-between">
                        <div>
                          <h4 className="text-lg font-medium text-slate-200">{team.name}</h4>
                          
                          <div className="mt-3">
                            <p className="text-sm text-slate-400 mb-1">Manager:</p>
                            <p className="text-sm text-slate-300">
                              {team.manager ? getUserNameById(team.manager) : 'None'}
                            </p>
                          </div>
                          
                          <div className="mt-4">
                            <p className="text-sm text-slate-400 mb-2">Team Members ({team.users.length}):</p>
                            {team.users.length > 0 ? (
                              <div className="flex flex-wrap gap-2">
                                {team.users.map(userId => (
                                  <span 
                                    key={userId} 
                                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs 
                                            bg-purple-900/30 text-purple-300 border border-purple-800/50"
                                  >
                                    <User className="w-3 h-3 mr-1" />
                                    {getUserNameById(userId)}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-slate-500 italic">No team members assigned</p>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex flex-col space-y-2 ml-4">
                          <button
                            onClick={() => handleEditTeam(team)}
                            className="p-2 text-slate-400 hover:text-slate-200 hover:bg-slate-600/30 rounded"
                            title="Edit Team"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          
                          <button
                            onClick={() => handleDeleteTeam(team.id)}
                            className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-600/30 rounded"
                            title="Delete Team"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                          
                          <button
                            onClick={() => handleOpenAssignUsers(team.id, 'team')}
                            className="p-2 text-slate-400 hover:text-green-400 hover:bg-slate-600/30 rounded"
                            title="Assign Users"
                          >
                            <UserPlus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Team Form Modal */}
              {showTeamForm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                  <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-lg w-full max-w-md">
                    <div className="p-4 border-b border-slate-700 flex justify-between items-center">
                      <h3 className="text-lg font-medium text-slate-200">
                        {editingTeam ? 'Edit Team' : 'Create New Team'}
                      </h3>
                      <button 
                        onClick={() => setShowTeamForm(false)}
                        className="text-slate-400 hover:text-slate-200"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    
                    <form onSubmit={handleTeamSubmit} className="p-6 space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                          Team Name*
                        </label>
                        <input
                          type="text"
                          value={teamForm.name}
                          onChange={(e) => setTeamForm({...teamForm, name: e.target.value})}
                          className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md 
                                  text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g. Engineering Team"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                          Team Manager*
                        </label>
                        <select
                          value={teamForm.manager}
                          onChange={(e) => setTeamForm({...teamForm, manager: e.target.value})}
                          className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md 
                                  text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        >
                          <option value="">Select Manager</option>
                          {users
                            .filter(user => user.role === 'admin' || user.role === 'manager')
                            .map(user => (
                              <option key={user.id} value={user.id}>
                                {user.full_name || user.username}
                              </option>
                            ))
                          }
                        </select>
                      </div>
                      
                      <div className="flex justify-end space-x-3 pt-4 border-t border-slate-700">
                        <button
                          type="button"
                          onClick={() => setShowTeamForm(false)}
                          className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 
                                   rounded-md transition-colors"
                        >
                          Cancel
                        </button>
                        
                        <button
                          type="submit"
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white 
                                   rounded-md transition-colors flex items-center"
                          disabled={loading}
                        >
                          {loading ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Processing...
                            </>
                          ) : (
                            <>
                              <Save className="w-4 h-4 mr-2" />
                              {editingTeam ? 'Update Team' : 'Create Team'}
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* User Assignment Modal */}
      {showAssignUsersModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-slate-700 flex justify-between items-center sticky top-0 bg-slate-800 z-10">
              <h3 className="text-lg font-medium text-slate-200">
                Assign Users to {assigningToType === 'shift' ? 'Shift' : 'Team'}
              </h3>
              <button 
                onClick={() => setShowAssignUsersModal(false)}
                className="text-slate-400 hover:text-slate-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              {/* Search Users */}
              <div className="mb-4 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchUserTerm}
                  onChange={(e) => setSearchUserTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-md 
                          text-slate-200 text-sm placeholder-slate-400
                          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              {/* User List */}
              <div className="border border-slate-600 rounded-md max-h-80 overflow-y-auto">
                {users
                  .filter(user => 
                    (user.full_name?.toLowerCase().includes(searchUserTerm.toLowerCase()) ||
                    user.username.toLowerCase().includes(searchUserTerm.toLowerCase()))
                  )
                  .map(user => (
                    <div 
                      key={user.id} 
                      className="flex items-center justify-between p-3 border-b border-slate-600 last:border-b-0"
                    >
                      <div className="flex items-center">
                        <User className="w-5 h-5 text-slate-400 mr-3" />
                        <div>
                          <p className="text-slate-200">{user.full_name || user.username}</p>
                          <p className="text-sm text-slate-400">{user.username}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center">
                        <span className={`
                          mr-3 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
                          ${user.role === 'admin' ? 'bg-red-900/30 text-red-300 border border-red-800/50' : 
                           user.role === 'manager' ? 'bg-yellow-900/30 text-yellow-300 border border-yellow-800/50' : 
                           'bg-blue-900/30 text-blue-300 border border-blue-800/50'}
                        `}>
                          {user.role}
                        </span>
                        
                        <input
                          type="checkbox"
                          checked={selectedUserIds.includes(user.id)}
                          onChange={() => toggleUserSelection(user.id)}
                          className="h-5 w-5 text-blue-600 focus:ring-blue-500 
                                    border-slate-500 rounded bg-slate-700"
                        />
                      </div>
                    </div>
                  ))}
              </div>
              
              <div className="flex justify-between items-center mt-4">
                <p className="text-sm text-slate-400">
                  Selected: {selectedUserIds.length} users
                </p>
                
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowAssignUsersModal(false)}
                    className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 
                            rounded-md transition-colors"
                  >
                    Cancel
                  </button>
                  
                  <button
                    type="button"
                    onClick={handleSaveAssignments}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white 
                            rounded-md transition-colors flex items-center"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Processing...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Assignments
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShiftsTeams;
